/* ──────────────────────────────────────────────────────────────────────────
   BimPillarPage.jsx — dedicated pillar layout for /bim/ (replaces the generic
   ServiceLandingPage for this route). Content sourced from
   window.DZ_SERVICE_LANDINGS['bim']. Uses the shared native kit (window.DZViz).

   Every infographic renders a FULL static baseline in the DOM (final diagram +
   real text) so the prerendered HTML and no-JS users get complete content;
   motion/interactivity is layered on top (IntersectionObserver + `.is-in-view`
   + a passive scroll listener for the signature). No GSAP. SVG, not canvas.
   Brand: Navy/Aqua, existing type scale. One <h1> (the hero).
   ────────────────────────────────────────────────────────────────────────── */
/* global React */
(function () {
  'use strict';
  const { useState, useRef, useEffect, useCallback } = React;
  const V = () => window.DZViz || {};

  /* ── Signature data: BIM dimensions 3D → 7D (established, sourced from the
       delivers items) + the emerging 8D–10D extensions (industry framings:
       safety, lean, industrialised construction — generic definitions, no DZ
       claims). ── */
  const DIMS = [
    { dim: '3D', title: 'Geometry', point: 'arch · struct · MEP federated',
      blurb: 'The coordinated, clash-checked model — architecture, structure, and MEP federated into one buildable whole.' },
    { dim: '4D', title: 'Schedule', point: 'linked to the build programme',
      blurb: 'Model elements linked to the construction programme, so the build sequence and phasing can be rehearsed.' },
    { dim: '5D', title: 'Cost', point: 'live BoQ + budget',
      blurb: 'Quantities and rates tied to model elements for a live bill of quantities and continuous budget tracking.' },
    { dim: '6D', title: 'Performance', point: 'energy + sustainability',
      blurb: 'Energy and sustainability performance carried in the model for analysis, reporting, and compliance.' },
    { dim: '7D', title: 'Operations', point: 'AIM → digital twin',
      blurb: 'The as-built Asset Information Model handed to facilities management — where the model becomes a live digital twin: a data-connected virtual replica of the running asset.' },
    { dim: '8D', title: 'Safety', point: 'safer sites · risk mitigation',
      blurb: 'Risk assessments and safety planning attached to model elements — hazards are identified and mitigated on the model before work reaches site.' },
    { dim: '9D', title: 'Lean construction', point: 'less waste · steadier flow',
      blurb: 'Productivity, workflow, and resource data layered onto the model to optimise construction flow — less waste, less idle time.' },
    { dim: '10D', title: 'Industrialisation', point: 'prefab · faster delivery',
      blurb: 'Prefabrication, modular, and automation data — the model drives off-site manufacture for faster, standardised delivery.' }
  ];

  /* ── ISO 19650 golden-thread nodes (sourced from approach + delivers CDE) ── */
  const THREAD = [
    { id: 'OIR', label: 'OIR', tip: 'Organizational Information Requirements — what the owning organization needs across its asset portfolio.' },
    { id: 'PIR', label: 'PIR', tip: 'Project Information Requirements — what this specific project must produce.' },
    { id: 'EIR', label: 'EIR', tip: 'Exchange Information Requirements — the data the model must carry, at what point, and in what format.' },
    { id: 'BEP', label: 'BEP', tip: 'BIM Execution Plan — roles, the delivery milestones (the project MIDP and each task team’s TIDP), federation strategy, and the naming convention.' },
    { id: 'CDE', label: 'CDE', tip: 'Common Data Environment — authored, shared, and approved through a controlled WIP → shared → published → archived flow.' },
    { id: 'VER', label: 'Verify', tip: 'Models are checked against the EIR and against each other before every gate.' },
    { id: 'HND', label: 'Handover', tip: 'As-built data survives into operation — ISO 19650-3 governs the operational phase.' }
  ];

  /* ── LOD stages ── */
  const LODS = [
    { value: '200', name: 'LOD 200', tag: 'Generic', blurb: 'Approximate geometry — size, shape, and location as placeholders. Enough to study massing and coordination intent.' },
    { value: '300', name: 'LOD 300', tag: 'Tender-ready', blurb: 'Specific geometry with accurate size, shape, location, and orientation — the level most tender and design deliverables require.' },
    { value: '400', name: 'LOD 400', tag: 'Fabrication-ready', blurb: 'Modeled with fabrication, assembly, and installation detail — ready for shop drawings and off-site manufacture.' }
  ];

  /* ── BIM door object properties (shared: figure callouts + a11y summary).
       y = callout row position as % of the scene height. ── */
  const DOOR_PROPS = [
    { y: 30, k: 'Fire',        v: 'FD60' },
    { y: 46, k: 'Acoustic',    v: '32 dB' },
    { y: 62, k: 'Ironmongery', v: 'set' },
    { y: 78, k: 'Cost',        v: 'element-linked' }
  ];

  /* ── little helpers ── */
  function Eyebrow({ children }) {
    return <span className="dz-eyebrow dz-bim-eyebrow">{children}</span>;
  }

  /* ═══════════════ SIGNATURE — BIM dimensions 3D → 10D build-up ═══════════════ */
  /* Real-project stage: the Visitors Pavilion at King Salman Park (a Design Zone
     project), rendered as a BIM digital twin — photoreal structure dissolving into
     its structural wireframe. Under the photo: a 3D…10D dimension rail that lights
     up as the scroll-story advances, and a READOUT slot where one large dimension
     infographic renders at a time (crossfade per step). Resting/no-JS/reduced-motion
     shows the final state (all chips lit, last panel visible). Rail + readout are
     decorative (aria-hidden) — the real content lives in the step cards. */
  function DimPanel({ i, title, live, children }) {
    return (
      <div className="dz-bim-dims__panel" data-i={i}>
        <span className="dz-bim-dims__panel-t">{live ? <span className="dz-bim-dims__chip-live" /> : null}{title}</span>
        <svg viewBox="0 0 320 96" focusable="false">{children}</svg>
      </div>
    );
  }
  function TwinFigure() {
    return (
      <figure className="dz-bim-dims__figure dz-bim-dims__figure--twin">
        <div className="dz-bim-dims__photo">
          <picture>
            <source type="image/avif" srcSet="/assets/bim/ksp-pavilion-twin-mobile.avif 820w, /assets/bim/ksp-pavilion-twin.avif 1600w" sizes="(max-width: 760px) 92vw, 600px" />
            <source type="image/webp" srcSet="/assets/bim/ksp-pavilion-twin-mobile.webp 820w, /assets/bim/ksp-pavilion-twin.webp 1600w" sizes="(max-width: 760px) 92vw, 600px" />
            <img src="/assets/bim/ksp-pavilion-twin.jpg" width="1600" height="894" loading="lazy" decoding="async"
                 alt="The Visitors Pavilion at King Salman Park — a Design Zone project — as a BIM digital twin: the photoreal structure dissolving into its glowing structural wireframe" />
          </picture>
        </div>
        <div className="dz-bim-dims__rail" aria-hidden="true">
          {DIMS.map((d, i) => (
            <span key={d.dim} className="dz-bim-dims__railchip" data-i={i}>{d.dim}</span>
          ))}
        </div>
        <div className="dz-bim-dims__readout" aria-hidden="true">
          <DimPanel i={0} title="3D · FEDERATED GEOMETRY">
            <g strokeLinejoin="round">
              <path d="M28 34 L98 34 L130 16 L60 16 Z" fill="#3E6C8A" fillOpacity="0.45" stroke="#3E6C8A" strokeWidth="1.2"/>
              <path d="M28 58 L98 58 L130 40 L60 40 Z" fill="#8FACC4" fillOpacity="0.30" stroke="#8FACC4" strokeWidth="1.2"/>
              <path d="M28 82 L98 82 L130 64 L60 64 Z" fill="#4FC8F4" fillOpacity="0.24" stroke="#4FC8F4" strokeWidth="1.2"/>
              <path d="M46 26 L46 74 M112 26 L112 74" stroke="#7FD8F7" strokeWidth="0.9" strokeDasharray="3 3" opacity="0.55" fill="none"/>
            </g>
            <g fontSize="10">
              <rect x="170" y="18" width="9" height="9" rx="2" fill="#3E6C8A"/><text x="186" y="26" fill="#bfe6fa">ARCHITECTURE</text>
              <rect x="170" y="42" width="9" height="9" rx="2" fill="#8FACC4"/><text x="186" y="50" fill="#bfe6fa">STRUCTURE</text>
              <rect x="170" y="66" width="9" height="9" rx="2" fill="#4FC8F4"/><text x="186" y="74" fill="#bfe6fa">MEP · CLASH-CHECKED</text>
            </g>
          </DimPanel>
          <DimPanel i={1} title="4D · BUILD SEQUENCE">
            <line x1="12" y1="14" x2="308" y2="14" stroke="#2f4f66" strokeWidth="1.2"/>
            <g stroke="#8FACC4" strokeWidth="0.8" opacity="0.6"><path d="M12 10 L12 18 M86 10 L86 18 M160 10 L160 18 M234 10 L234 18 M308 10 L308 18" fill="none"/></g>
            <rect x="12" y="28" width="118" height="10" rx="5" fill="#3E6C8A"/>
            <rect x="96" y="46" width="132" height="10" rx="5" fill="#4FC8F4" opacity="0.85"/>
            <rect x="196" y="64" width="100" height="10" rx="5" fill="#8FACC4"/>
            <line x1="160" y1="22" x2="160" y2="82" stroke="#7FD8F7" strokeWidth="1" strokeDasharray="4 3"/>
            <text x="167" y="88" fill="#7FD8F7" fontSize="9.5">today</text>
          </DimPanel>
          <DimPanel i={2} title="5D · BoQ · COST">
            <g>
              <rect x="12" y="12" width="96" height="9" rx="4.5" fill="#3E6C8A"/><rect x="120" y="12" width="58" height="9" rx="4.5" fill="#22405a"/><rect x="190" y="12" width="42" height="9" rx="4.5" fill="#22405a"/>
              <rect x="12" y="30" width="96" height="9" rx="4.5" fill="#3E6C8A"/><rect x="120" y="30" width="44" height="9" rx="4.5" fill="#22405a"/><rect x="176" y="30" width="56" height="9" rx="4.5" fill="#22405a"/>
              <rect x="12" y="48" width="96" height="9" rx="4.5" fill="#3E6C8A"/><rect x="120" y="48" width="66" height="9" rx="4.5" fill="#22405a"/><rect x="198" y="48" width="34" height="9" rx="4.5" fill="#22405a"/>
            </g>
            <line x1="12" y1="68" x2="232" y2="68" stroke="#2f4f66" strokeWidth="1"/>
            <text x="12" y="86" fill="#7FD8F7" fontSize="11">Σ element-linked · live BoQ</text>
            <text x="308" y="86" textAnchor="end" fill="#bfe6fa" fontSize="10">rates × quantities</text>
          </DimPanel>
          <DimPanel i={3} title="6D · ENERGY & LIFECYCLE">
            <rect x="12" y="12" width="150" height="12" rx="3" fill="#2E7D5B"/>
            <rect x="12" y="30" width="122" height="12" rx="3" fill="#5aa06a"/>
            <rect x="12" y="48" width="94" height="12" rx="3" fill="#c9a24b"/>
            <rect x="12" y="66" width="66" height="12" rx="3" fill="#c77b4b"/>
            <rect x="210" y="22" width="46" height="36" rx="4" fill="#2E7D5B"/>
            <text x="233" y="47" textAnchor="middle" fill="#eafff2" fontSize="21" fontWeight="700">A</text>
            <text x="308" y="80" textAnchor="end" fill="#bfe6fa" fontSize="10">performance in the model</text>
          </DimPanel>
          <DimPanel i={4} title="7D · LIVE TWIN" live>
            <text x="308" y="16" textAnchor="end" fill="#bfe6fa" fontSize="10.5">AIM → FM</text>
            <polyline points="12,72 52,58 92,64 132,42 172,52 212,34 252,44 296,26" fill="none" stroke="#4FC8F4" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="296" cy="26" r="3.4" fill="#4FC8F4"/>
            <text x="12" y="90" fill="#bfe6fa" fontSize="10">as-built data · live in operation</text>
          </DimPanel>
          <DimPanel i={5} title="8D · SAFETY & RISK · EMERGING">
            <path d="M30 44 L48 12 L66 44 Z" fill="none" stroke="#c9a24b" strokeWidth="2" strokeLinejoin="round"/>
            <path d="M48 22 L48 33 M48 38 L48 40" stroke="#c9a24b" strokeWidth="2" strokeLinecap="round" fill="none"/>
            <g fill="none" stroke="#57C98C" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="96" y="12" width="13" height="13" rx="3"/><path d="M99.5 18.5 L102.5 21.5 L106.5 14.5"/>
              <rect x="96" y="36" width="13" height="13" rx="3"/><path d="M99.5 42.5 L102.5 45.5 L106.5 38.5"/>
              <rect x="96" y="60" width="13" height="13" rx="3"/><path d="M99.5 66.5 L102.5 69.5 L106.5 62.5"/>
            </g>
            <g>
              <rect x="122" y="15" width="158" height="8" rx="4" fill="#22405a"/>
              <rect x="122" y="39" width="128" height="8" rx="4" fill="#22405a"/>
              <rect x="122" y="63" width="142" height="8" rx="4" fill="#22405a"/>
            </g>
            <text x="30" y="90" fill="#e7c77e" fontSize="10">hazards mitigated on the model — before site</text>
          </DimPanel>
          <DimPanel i={6} title="9D · LEAN FLOW · EMERGING">
            <g stroke="#8FACC4" strokeWidth="1.3" fill="none">
              <rect x="12" y="26" width="64" height="28" rx="6"/>
              <rect x="112" y="26" width="64" height="28" rx="6"/>
              <rect x="212" y="26" width="64" height="28" rx="6"/>
            </g>
            <g stroke="#4FC8F4" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <path d="M76 40 L112 40 M105 35 L112 40 L105 45"/>
              <path d="M176 40 L212 40 M205 35 L212 40 L205 45"/>
            </g>
            <polyline points="240,84 262,76 284,64 304,50" fill="none" stroke="#57C98C" strokeWidth="2" strokeLinecap="round"/>
            <path d="M304 50 L295 51 M304 50 L302 58" stroke="#57C98C" strokeWidth="2" strokeLinecap="round" fill="none"/>
            <text x="12" y="86" fill="#bfe6fa" fontSize="10">takt flow · resource-levelled · less waste</text>
          </DimPanel>
          <DimPanel i={7} title="10D · MODULAR & PREFAB · EMERGING">
            <g stroke="#8FACC4" strokeWidth="1.2" fill="#1b3247">
              <rect x="12" y="52" width="44" height="30" rx="3"/>
              <rect x="60" y="52" width="44" height="30" rx="3"/>
              <rect x="108" y="52" width="44" height="30" rx="3"/>
              <rect x="12" y="18" width="44" height="30" rx="3"/>
              <rect x="60" y="18" width="44" height="30" rx="3"/>
            </g>
            <path d="M130 0 L130 10" stroke="#8FACC4" strokeWidth="1.2" fill="none"/>
            <rect x="108" y="10" width="44" height="30" rx="3" fill="rgba(79,200,244,0.08)" stroke="#4FC8F4" strokeWidth="1.3" strokeDasharray="5 4"/>
            <text x="176" y="34" fill="#bfe6fa" fontSize="10">prefab · off-site → on-site</text>
            <text x="176" y="52" fill="#bfe6fa" fontSize="10">standardised · faster delivery</text>
          </DimPanel>
        </div>
        <figcaption className="dz-bim-dims__credit">
          <span className="dz-bim-dims__credit-dim">3D–7D · 8D–10D emerging</span>
          <span>Visitors Pavilion · King Salman Park — a Design Zone project</span>
        </figcaption>
      </figure>
    );
  }

  function BimDimensions() {
    const secRef = useRef(null);
    const onStep = useCallback((step) => {
      const sec = secRef.current; if (!sec) return;
      sec.querySelectorAll('.dz-bim-dims__panel').forEach((g) => {
        g.classList.toggle('is-now', Number(g.getAttribute('data-i')) === step);
      });
      sec.querySelectorAll('.dz-bim-dims__railchip').forEach((c) => {
        const i = Number(c.getAttribute('data-i'));
        c.classList.toggle('is-lit', i <= step);
        c.classList.toggle('is-now', i === step);
      });
      sec.querySelectorAll('.dz-bim-dims__step').forEach((li) => {
        const i = Number(li.getAttribute('data-i'));
        if (i === step) li.setAttribute('aria-current', 'step'); else li.removeAttribute('aria-current');
        li.classList.toggle('is-active', i <= step);
        li.classList.toggle('is-current', i === step);
      });
    }, []);
    const hookRef = V().useScrollStory(DIMS.length, onStep);
    const setRef = useCallback((n) => { secRef.current = n; if (hookRef) hookRef.current = n; }, [hookRef]);

    return (
      <section className="dz-bim-dims" ref={setRef} aria-labelledby="dz-bim-dims-h">
        <div className="dz-bim-dims__head">
          <Eyebrow>THE SIGNATURE</Eyebrow>
          <h2 id="dz-bim-dims-h" className="dz-bim-h2">One model — 3D to 7D. Watch it build.</h2>
          <p className="dz-bim-lead">A BIM model gains a data layer at each dimension — from coordinated geometry to a living operational twin, and on to the emerging 8D–10D: safety, lean flow, and industrialised delivery. Scroll to build it up, layer by layer, on one of our own projects: the Visitors Pavilion at King Salman Park.</p>
        </div>
        <div className="dz-bim-dims__grid">
          <TwinFigure />
          <ol className="dz-bim-dims__list">
            {DIMS.map((d, i) => (
              <li className={'dz-bim-dims__step' + (i === 0 ? ' is-active' : '')} data-i={i} key={d.dim}>
                <div className="dz-bim-dims__step-inner">
                  <span className="dz-bim-dims__dim">{d.dim}</span>
                  <h3 className="dz-bim-dims__title">{d.title}</h3>
                  <p className="dz-bim-dims__blurb">{d.blurb}</p>
                  <span className="dz-bim-dims__point">{d.point}</span>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
    );
  }

  /* ═══════════════ BIM vs CAD — draggable before/after ═══════════════ */
  /* ONE door, two skins. Identical geometry in both panes, centered on the 50%
     seam, so the wipe literally converts the same linework into a data object.
     The SVG carries only geometry (viewBox 680x400 = the stage's exact 1.7:1
     aspect, filling the pane edge-to-edge); every piece of text is HTML
     positioned in %, clipped with the pane — crisp at all sizes, never cropped.
     CAD skin: grey drafting (dims, crosses, swing arc). BIM skin: the same
     geometry in aqua with leaf fill, hardware, and the four data callouts. */
  function DoorScene({ bim }) {
    const ink = bim ? '#4FC8F4' : '#7f97a9';
    return (
      <div className={bim ? 'dz-bim-bim' : 'dz-bim-cad'}>
        <svg viewBox="0 0 680 400" aria-hidden="true" focusable="false">
          {bim ? (
            <defs>
              <linearGradient id="dzBimDoor" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#24405A"/><stop offset="1" stopColor="#16293A"/>
              </linearGradient>
            </defs>
          ) : null}
          {/* floor line */}
          <line x1="170" y1="352" x2="510" y2="352" stroke={ink} strokeWidth="1.2" opacity={bim ? 0.7 : 0.45}/>
          {/* leaf fill (BIM only) */}
          {bim ? <rect x="277" y="65" width="126" height="287" fill="url(#dzBimDoor)"/> : null}
          {/* frame + leaf + panels — shared geometry, centered on x=340 */}
          <g stroke={ink} strokeWidth="1.4" fill="none" strokeLinejoin="round">
            <path d="M264 352 L264 52 L416 52 L416 352"/>
            <path d="M277 352 L277 65 L403 65 L403 352"/>
            <rect x="293" y="89" width="94" height="98" fill={bim ? '#1b3247' : 'none'} strokeWidth={bim ? 1 : 1.1}/>
            <rect x="293" y="215" width="94" height="98" fill={bim ? '#1b3247' : 'none'} strokeWidth={bim ? 1 : 1.1}/>
          </g>
          {/* swing arc — the drafting symbol both formats share */}
          <path d="M403 352 A 126 126 0 0 0 277 226" fill="none" stroke={ink} strokeWidth="1" strokeDasharray="5 6" opacity="0.45"/>
          {/* hardware: outlines in CAD, real filled ironmongery in BIM */}
          {bim ? (
            <g fill="#4FC8F4">
              <rect x="272" y="92" width="7" height="20" rx="1.5"/>
              <rect x="272" y="196" width="7" height="20" rx="1.5"/>
              <rect x="272" y="300" width="7" height="20" rx="1.5"/>
              <circle cx="392" cy="206" r="4"/>
              <rect x="368" y="203.8" width="24" height="4.4" rx="2.2"/>
              <rect x="330" y="70" width="46" height="9" rx="2" fillOpacity="0.9"/>
            </g>
          ) : (
            <g stroke={ink} strokeWidth="1.1" fill="none">
              <rect x="272" y="92" width="7" height="20"/>
              <rect x="272" y="196" width="7" height="20"/>
              <rect x="272" y="300" width="7" height="20"/>
              <circle cx="392" cy="206" r="4"/>
              <line x1="368" y1="206" x2="388" y2="206"/>
            </g>
          )}
          {bim ? (
            <g stroke="#4FC8F4" fill="none">
              {/* callout leaders — rows match DOOR_PROPS y% of the 400-unit height */}
              <g strokeWidth="1.1">
                <line x1="403" y1="120" x2="452" y2="120"/><circle cx="403" cy="120" r="3" fill="#4FC8F4" stroke="none"/>
                <line x1="403" y1="184" x2="452" y2="184"/><circle cx="403" cy="184" r="3" fill="#4FC8F4" stroke="none"/>
                <line x1="403" y1="248" x2="452" y2="248"/><circle cx="403" cy="248" r="3" fill="#4FC8F4" stroke="none"/>
                <line x1="403" y1="312" x2="452" y2="312"/><circle cx="403" cy="312" r="3" fill="#4FC8F4" stroke="none"/>
              </g>
              {/* BIM keeps the geometry too — the same dims, now aqua */}
              <g strokeWidth="0.9" opacity="0.5">
                <line x1="236" y1="52" x2="236" y2="352"/>
                <line x1="230" y1="52" x2="242" y2="52"/><line x1="230" y1="352" x2="242" y2="352"/>
                <line x1="264" y1="374" x2="416" y2="374"/>
                <line x1="264" y1="368" x2="264" y2="380"/><line x1="416" y1="368" x2="416" y2="380"/>
              </g>
            </g>
          ) : (
            <g stroke={ink} strokeWidth="0.9" fill="none" opacity="0.75">
              {/* vertical dimension */}
              <line x1="236" y1="52" x2="236" y2="352"/>
              <line x1="230" y1="52" x2="242" y2="52"/><line x1="230" y1="352" x2="242" y2="352"/>
              <path d="M236 52 L232 62 M236 52 L240 62 M236 352 L232 342 M236 352 L240 342"/>
              {/* horizontal dimension */}
              <line x1="264" y1="374" x2="416" y2="374"/>
              <line x1="264" y1="368" x2="264" y2="380"/><line x1="416" y1="368" x2="416" y2="380"/>
              {/* drafting crosses */}
              <path d="M186 84 L198 84 M192 78 L192 90" opacity="0.5"/>
              <path d="M478 300 L490 300 M484 294 L484 306" opacity="0.5"/>
            </g>
          )}
        </svg>
        {bim ? (
          <React.Fragment>
            {DOOR_PROPS.map((c) => (
              <span key={c.k} className="dz-bim-scene__co" style={{ top: c.y + '%' }}>
                <span className="dz-bim-bim__k">{c.k}</span>
                <span className="dz-bim-bim__v">{c.v}</span>
              </span>
            ))}
            {/* same dims as the CAD skin, same coordinates — the seam wipes the ink */}
            <span className="dz-bim-scene__dim dz-bim-scene__dim--bim" style={{ left: '30.5%', top: '50%' }}>2100</span>
            <span className="dz-bim-scene__dim dz-bim-scene__dim--bim" style={{ left: '50%', top: '96%' }}>1000</span>
            <span className="dz-bim-scene__cap dz-bim-scene__cap--bim">an object that knows its data</span>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <span className="dz-bim-scene__dim" style={{ left: '30.5%', top: '50%' }}>2100</span>
            <span className="dz-bim-scene__dim" style={{ left: '50%', top: '96%' }}>1000</span>
            <span className="dz-bim-scene__cap dz-bim-scene__cap--cad">flat linework · no data behind it</span>
          </React.Fragment>
        )}
      </div>
    );
  }
  function BimVsCad({ point }) {
    const ref = V().useReveal(0.2);
    const Slider = V().BeforeAfterSlider;
    return (
      <section className="dz-bim-vs dz-viz-reveal" ref={ref} aria-labelledby="dz-bim-vs-h">
        <div className="dz-bim-vs__intro">
          <Eyebrow>BIM VS CAD</Eyebrow>
          <h2 id="dz-bim-vs-h" className="dz-bim-h2">{point.h}</h2>
          <p className="dz-bim-lead">{point.p}</p>
          <p className="dz-bim-vs__hint">Drag the handle — or use the slider with your keyboard.</p>
        </div>
        <div className="dz-bim-vs__stage">
          <div className="dz-bim-vp">
            <div className="dz-bim-bar" aria-hidden="true"><span className="dz-bim-bar__dot" />VIEWPORT — D-101 · DOOR<span className="dz-bim-bar__crumb">SESSION · COMPARE</span></div>
            <Slider
              ariaLabel="Reveal the BIM object (D-101.rfa) over the CAD drawing (D-101.dwg)"
              beforeLabel="D-101.dwg" afterLabel="D-101.rfa"
              before={<DoorScene />}
              after={<DoorScene bim />}
            />
            <div className="dz-bim-foot"><span aria-hidden="true">↳</span> every element in the federated model carries data like this.</div>
          </div>
        </div>
        <p className="dz-viz-ba__sr">
          {'The same door in two file formats — as a CAD drawing (D-101.dwg) it is flat linework with no data behind it; as a BIM object (D-101.rfa) it is data-rich — ' +
            DOOR_PROPS.map((c) => c.k + ': ' + c.v).join(', ') + '.'}
        </p>
      </section>
    );
  }

  /* ═══════════════ ISO 19650 golden-thread flowline ═══════════════ */
  function GoldenThread({ approach }) {
    const ref = V().useReveal(0.25);
    const [open, setOpen] = useState(-1);
    const N = THREAD.length;
    // node x positions at the 7 grid-column centres of the overlay (0..1000 viewBox)
    const xs = THREAD.map((_, i) => (i + 0.5) * (1000 / N));
    const y = 60;
    const d = 'M' + xs.map((x) => x.toFixed(1) + ' ' + y).join(' L ');
    return (
      <section className="dz-bim-thread dz-viz-reveal" ref={ref} aria-labelledby="dz-bim-thread-h">
        <div className="dz-bim-thread__head">
          <Eyebrow>{approach.eyebrow}</Eyebrow>
          <h2 id="dz-bim-thread-h" className="dz-bim-h2">The golden thread — one governed information chain.</h2>
          <p className="dz-bim-lead">ISO 19650 governs how project information is specified, produced, and exchanged. It runs as an unbroken thread from the owner’s requirements to operational handover.</p>
          <p className="dz-bim-vs__hint">Hover or tap each stage to see what it governs.</p>
        </div>
        <div className="dz-bim-thread__viz">
          <svg viewBox="0 0 1000 120" className="dz-bim-thread__svg" aria-hidden="true" focusable="false" preserveAspectRatio="xMidYMid meet">
            <path className="dz-bim-thread__line" d={d} pathLength="1" fill="none" stroke="#4FC8F4" strokeWidth="2" strokeLinecap="round"/>
            {xs.map((x, i) => (
              <circle key={i} cx={x} cy={y} r="5" fill="#0E2030" stroke="#4FC8F4" strokeWidth="2" className="dz-bim-thread__dot"/>
            ))}
          </svg>
          <ol className="dz-bim-thread__nodes" onKeyDown={(e) => { if (e.key === 'Escape') setOpen(-1); }}>
            {THREAD.map((n, i) => (
              <li className="dz-bim-thread__node" key={n.id} style={{ '--ni': i }}>
                <button type="button" className={'dz-bim-thread__btn' + (open === i ? ' is-open' : '')}
                        aria-expanded={open === i} aria-describedby={'dz-thread-tip-' + i}
                        onClick={() => setOpen(open === i ? -1 : i)}
                        onMouseEnter={() => setOpen(i)} onMouseLeave={() => setOpen(-1)} onFocus={() => setOpen(i)}>
                  <span className="dz-bim-thread__num">{i + 1}</span>
                  <span className="dz-bim-thread__lbl">{n.label}</span>
                </button>
                <span aria-hidden="true" className="dz-bim-thread__tip">{n.tip}</span>
                <span id={'dz-thread-tip-' + i} className="dz-viz-ba__sr">{n.tip}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="dz-bim-thread__payoff">
          <picture>
            <source type="image/avif" srcSet="/assets/projects/digital-twin-dz-hq-mobile.avif" />
            <img src="/assets/projects/digital-twin-dz-hq.jpg" width="1600" height="944" loading="lazy" decoding="async"
                 alt="Digital twin of the Design Zone headquarters" />
          </picture>
          <p>
            <span className="dz-bim-thread__payoff-k">AFTER HANDOVER</span>
            The data that survives the thread keeps working after completion — the as-built model becomes a live digital twin, as on our own headquarters. <a href="/twinms/">See TwinMS</a>.
          </p>
        </div>
      </section>
    );
  }

  /* ═══════════════ LOD 200 → 300 → 400 selector ═══════════════ */
  function LodBricks({ level }) {
    // ONE wall element that matures in place: LOD 200 massing -> LOD 300 accurate
    // geometry (door/window/slab/column) -> LOD 400 fabrication (layers/fixings/dims).
    // Cumulative via data-level + CSS. SSR-safe; all three defined in the list below.
    return (
      <svg viewBox="0 0 200 150" className="dz-bim-lod__svg" data-level={level} aria-hidden="true" focusable="false">
        {/* ── LOD 200 — approximate massing on a slab ── */}
        <g className="dz-bim-lod__base">
          <path d="M26 128 L150 128 L178 112 L54 112 Z" fill="#12222f" stroke="#3a5972" strokeWidth="1"/>
          <path d="M60 112 L140 112 L140 44 L60 44 Z" fill="url(#dzBimWall2)" stroke="#6f8798" strokeWidth="1.2"/>
          <path d="M60 44 L140 44 L158 33 L78 33 Z" fill="#2a465f" stroke="#6f8798" strokeWidth="1.2"/>
          <path d="M140 112 L158 101 L158 33 L140 44 Z" fill="#1b3247" stroke="#6f8798" strokeWidth="1.2"/>
          <path d="M74 112 L104 112 L104 66 L74 66 Z" fill="#0e1c28" stroke="#6f8798" strokeWidth="1"/>
        </g>
        {/* ── LOD 300 — accurate geometry: door, window, slab, column ── */}
        <g className="dz-bim-lod__d300" stroke="#4FC8F4" fill="none">
          <rect x="74" y="66" width="30" height="46" strokeWidth="1.3"/>
          <rect x="78" y="70" width="22" height="42" strokeWidth="0.8"/>
          <circle cx="82" cy="90" r="1.6" fill="#4FC8F4"/>
          <rect x="112" y="58" width="24" height="30" strokeWidth="1.3"/>
          <path d="M124 58 L124 88 M112 73 L136 73" strokeWidth="0.7"/>
          <path d="M140 112 L140 44" strokeWidth="2.4" stroke="#8FACC4"/>
          <path d="M60 112 L140 112" strokeWidth="1.5" stroke="#8FACC4"/>
        </g>
        {/* ── LOD 400 — fabrication: wall build-up, hinges, base fixings, dims ── */}
        <g className="dz-bim-lod__d400" stroke="#7FD8F7" fill="none">
          <path d="M64 41 L144 41 M68 38 L148 38 M72 35.5 L152 35.5" strokeWidth="0.6"/>
          <path d="M74 74 L78 74 M74 104 L78 104" strokeWidth="1.2"/>
          <rect x="95" y="87" width="4" height="6" strokeWidth="0.7"/>
          <path d="M132 112 L148 112" strokeWidth="1.8" stroke="#7FD8F7"/>
          <circle cx="136" cy="110" r="1" fill="#7FD8F7"/><circle cx="144" cy="110" r="1" fill="#7FD8F7"/>
          <path d="M60 138 L140 138 M60 135 L60 141 M140 135 L140 141" strokeWidth="0.7"/>
          <path d="M63 138 L66 136 M63 138 L66 140 M137 138 L134 136 M137 138 L134 140" strokeWidth="0.7"/>
        </g>
      </svg>
    );
  }
  function LodProgression() {
    const ref = V().useReveal(0.2);
    const [level, setLevel] = useState('300');
    const Seg = V().SegmentedControl;
    const active = LODS.find((l) => l.value === level) || LODS[1];
    return (
      <section className="dz-bim-lod dz-viz-reveal" ref={ref} aria-labelledby="dz-bim-lod-h">
        <div className="dz-bim-lod__head">
          <Eyebrow>LEVEL OF DEVELOPMENT</Eyebrow>
          <h2 id="dz-bim-lod-h" className="dz-bim-h2">LOD 200 → 300 → 400 — the same element, resolved.</h2>
          <p className="dz-bim-lead">Watch one wall element mature: rough massing becomes accurate geometry — walls, door, window, slab, column — then fabrication-ready detail with layers, fixings and dimensions. Choose a level, or read all three below.</p>
        </div>
        <div className="dz-bim-lod__grid">
          <div className="dz-bim-lod__stage" data-level={level}>
            <div className="dz-bim-bar" aria-hidden="true"><span className="dz-bim-bar__dot" />ELEMENT VIEW — EXTERNAL WALL W-01<span className="dz-bim-bar__crumb">SESSION · RESOLVE</span></div>
            <svg width="0" height="0" aria-hidden="true"><defs><linearGradient id="dzBimWall2" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#24405A"/><stop offset="1" stopColor="#16293A"/></linearGradient></defs></svg>
            <LodBricks level={level} />
            <div className="dz-bim-lod__status" aria-hidden="true">{active.name} · {active.tag.toUpperCase()}</div>
            <Seg ariaLabel="Choose Level of Development"
                 options={LODS.map((l) => ({ value: l.value, label: l.name }))}
                 value={level} onChange={setLevel} />
          </div>
          <ul className="dz-bim-lod__defs">
            {LODS.map((l) => (
              <li key={l.value} className={'dz-bim-lod__def' + (l.value === level ? ' is-active' : '')}>
                <span className="dz-bim-lod__deftag">{l.name} · {l.tag}</span>
                <p>{l.blurb}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>
    );
  }

  /* ═══════════════ Disciplines hub-and-spoke ═══════════════ */
  /* file-type chips: the format each discipline actually appends to the federation */
  const HUB_MARKS = {
    '/arch-bim/': '.RVT', '/mep-bim/': '.RVT', '/hbim/': '.RCP',
    '/scan-to-bim/': '.E57', '/clash-detection/': '.BCF', '/iso-19650/': '19650', '/services/': '01–11'
  };
  function DisciplinesHub({ heading, stages }) {
    const ref = V().useReveal(0.2);
    const [hot, setHot] = useState(-1);
    const N = stages.length;
    return (
      <section className="dz-bim-hub dz-viz-reveal" ref={ref} aria-labelledby="dz-bim-hub-h">
        <div className="dz-bim-hub__head">
          <Eyebrow>{heading.eyebrow}</Eyebrow>
          <h2 id="dz-bim-hub-h" className="dz-bim-h2">{heading.titleLead} <em>{heading.titleAccent}</em></h2>
          {heading.ctaLink && <p className="dz-cta-link">{heading.ctaLink.text} <a href={heading.ctaLink.href}>{heading.ctaLink.label} →</a></p>}
        </div>
        <div className={'dz-bim-hub__wheel' + (hot >= 0 ? ' is-hot' : '')} style={{ '--dz-hub-n': N }}>
          <svg className="dz-bim-hub__lines" viewBox="0 0 100 100" aria-hidden="true" focusable="false" preserveAspectRatio="none">
            {stages.map((_, i) => {
              const a = (i / N) * Math.PI * 2 - Math.PI / 2;
              return <line key={i} x1="50" y1="50" x2={(50 + Math.cos(a) * 46).toFixed(2)} y2={(50 + Math.sin(a) * 39).toFixed(2)}
                           pathLength="1" style={{ '--hi': i }}
                           strokeWidth="0.5" className={'dz-bim-hub__line' + (hot === i ? ' is-hot' : '')}/>;
            })}
          </svg>
          <div className="dz-bim-hub__core" aria-hidden="true"><span className="dz-bim-hub__core-lbl">BIM</span><span className="dz-bim-hub__core-dots" /></div>
          <ul className="dz-bim-hub__spokes">
            {stages.map((s, i) => {
              const a = (i / N) * Math.PI * 2 - Math.PI / 2;
              const x = 50 + Math.cos(a) * 46, y = 50 + Math.sin(a) * 39;
              const mark = HUB_MARKS[s.href] || s.num;
              const isFile = /^\./.test(mark);
              return (
                <li key={s.href} className={'dz-bim-hub__spoke' + (hot === i ? ' is-hot' : '')}
                    style={{ '--dz-x': x + '%', '--dz-y': y + '%', '--hi': i }}>
                  <a href={s.href} className="dz-bim-hub__link"
                     onMouseEnter={() => setHot(i)} onMouseLeave={() => setHot(-1)}
                     onFocus={() => setHot(i)} onBlur={() => setHot(-1)}>
                    <span className="dz-bim-hub__disc">
                      <span className="dz-bim-hub__disc-in">
                        <span className={'dz-bim-hub__mark dz-bim-hub__mark--chip ' + (isFile ? 'is-file' : 'is-concept')} aria-hidden="true">{mark}</span>
                        <span className="dz-bim-hub__spoke-lbl">{s.label}</span>
                      </span>
                    </span>
                    <span className="dz-bim-hub__note">{s.note}</span>
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    );
  }

  /* ═══════════════ Supporting sections ═══════════════ */
  const HERO_PROOF = [
    { k: 'ISO 19650-2 certified', v: 'Audited to the standard — not merely “familiar” with it.' },
    { k: 'Bilingual · EN / AR', v: 'Tenders and authority submissions delivered in both.' },
    { k: 'Federated, multi-discipline', v: 'Architecture, structure and MEP coordinated into one clash-checked model.' },
    { k: 'End to end', v: 'Brief → coordinated model → fabrication → operational handover.' }
  ];
  function Hero({ hero }) {
    const scrollToForm = (e) => {
      const cta = document.querySelector('.dz-cta');
      if (cta) { e.preventDefault(); cta.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    };
    return (
      <header className="dz-bim-hero">
        <div className="dz-bim-hero__inner">
          <div className="dz-bim-hero__grid">
            <div className="dz-bim-hero__copy">
              <Eyebrow>{hero.eyebrow}</Eyebrow>
              <h1 className="dz-bim-hero__h1">
                <span className="dz-bim-hero__lead">{hero.title.lead}</span>{' '}
                <span className="dz-bim-hero__accent">{hero.title.accent}</span>
              </h1>
              <p className="dz-bim-hero__sub">{hero.sub}</p>
              <div className="dz-bim-hero__cta">
                <a className="dz-bim-btn dz-bim-btn--primary" href={(hero.ctas && hero.ctas.primary.href) || '/contact/?topic=bim'} onClick={scrollToForm}>
                  {(hero.ctas && hero.ctas.primary.label) || 'Start a project'}
                  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h9M8 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </a>
                <a className="dz-bim-btn dz-bim-btn--ghost" href={(hero.ctas && hero.ctas.secondary.href) || '/services/'}>{(hero.ctas && hero.ctas.secondary.label) || 'See the 11-stage lifecycle'}</a>
              </div>
            </div>
            <aside className="dz-bim-hero__session">
              <div className="dz-bim-bar" aria-hidden="true"><span className="dz-bim-bar__dot" />MODEL SESSION — VISITORS PAVILION · KSP<span className="dz-bim-bar__crumb">SESSION · OPEN</span></div>
              <picture>
                <source type="image/avif" srcSet="/assets/projects/visitors-pavilion-ksp-mobile.avif 800w, /assets/projects/visitors-pavilion-ksp.avif 1600w" sizes="(max-width: 900px) 92vw, 520px" />
                <img src="/assets/projects/visitors-pavilion-ksp.jpg" width="1600" height="938" loading="eager" fetchPriority="high" decoding="async"
                     alt="The Visitors Pavilion at King Salman Park — a Design Zone project" />
              </picture>
              <div className="dz-bim-foot"><span aria-hidden="true">↳</span> scroll — the model gains a data layer at every step.</div>
            </aside>
          </div>
          <ul className="dz-bim-hero__proof">
            {HERO_PROOF.map((it, i) => (
              <li key={i}><span className="dz-bim-hero__proof-k">{it.k}</span><span className="dz-bim-hero__proof-v">{it.v}</span></li>
            ))}
          </ul>
        </div>
        <div className="dz-bim-hero__glow" aria-hidden="true" />
      </header>
    );
  }

  const WHAT_PROPS = [
    ['Material', 'Concrete C40'],
    ['Thickness', '300 mm'],
    ['Fire rating', 'REI 120'],
    ['U-value', '0.18 W/m²K'],
    ['Cost', 'element-linked'],
    ['Manufacturer', 'specified'],
    ['Phase', 'to programme'],
    ['Warranty', 'tracked']
  ];
  function WhatIs({ problem }) {
    const ref = V().useReveal(0.2);
    const defined = problem.points[0], why = problem.points[2];
    return (
      <section className="dz-bim-what dz-viz-reveal" ref={ref} aria-labelledby="dz-bim-what-h">
        <div className="dz-bim-what__head">
          <Eyebrow>{problem.eyebrow}</Eyebrow>
          <h2 id="dz-bim-what-h" className="dz-bim-h2">{problem.title}</h2>
        </div>
        <div className="dz-bim-what__grid">
          <div className="dz-bim-what__body">
            <div className="dz-bim-what__def">
              <h3 className="dz-bim-h3">{defined.h}</h3>
              <p>{defined.p}</p>
            </div>
            <div className="dz-bim-what__why">
              <h3 className="dz-bim-h3">{why.h}</h3>
              <p>{why.p}</p>
            </div>
            {problem.ctaLink && <p className="dz-cta-link">{problem.ctaLink.text} <a href={problem.ctaLink.href}>{problem.ctaLink.label} →</a></p>}
          </div>
          <figure className="dz-bim-what__panel" aria-hidden="true">
            <div className="dz-bim-what__panel-head"><span className="dz-bim-what__panel-dot" />ELEMENT · PROPERTIES<span className="dz-bim-bar__crumb">SESSION · DEFINE</span></div>
            <div className="dz-bim-what__sel"><span>1 selected</span><span className="dz-bim-what__sel-type">Basic Wall : DZ_EXT-ConcIns-300</span></div>
            <div className="dz-bim-what__panel-rows">
              {WHAT_PROPS.map(([k, v], i) => (
                <div key={k} className="dz-bim-what__prop-row dz-bim-boot" style={{ '--bi': i }}><span className="dz-bim-what__prop">{k}</span><span className="dz-bim-what__val">{v}</span></div>
              ))}
            </div>
            <div className="dz-bim-what__guid">IfcWallStandardCase · GUID 3vB2hO$fT0KujmVzLdqEc6</div>
            <div className="dz-bim-what__panel-foot"><span>↳</span> a CAD line stores none of this.</div>
          </figure>
        </div>
      </section>
    );
  }

  function vocabGlyph(term) {
    if (/Development|\bLOD\b/.test(term)) return <path d="M12 3 L21 8 L12 13 L3 8 Z M3 12 L12 17 L21 12 M3 16 L12 21 L21 16"/>;
    if (/Information Need|LOIN/.test(term)) return <g><circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3.2"/><path d="M12 2 L12 5 M12 19 L12 22 M2 12 L5 12 M19 12 L22 12"/></g>;
    return <g><rect x="3.5" y="5" width="17" height="14" rx="2"/><path d="M3.5 9 L20.5 9 M9 5 L9 19"/><circle cx="15" cy="14" r="1.5" fill="currentColor" stroke="none"/></g>;
  }
  /* per-term micro-instrument: the state/segment strip a practitioner recognizes */
  function vocabStrip(term) {
    if (/Development|\bLOD\b/.test(term)) return (
      <span className="dz-bim-vocab__strip" aria-hidden="true"><i>200</i><em>·</em><i className="is-on">300</i><em>·</em><i>400</i></span>
    );
    if (/Information Need|LOIN/.test(term)) return (
      <span className="dz-bim-vocab__strip" aria-hidden="true"><i>geometrical</i><em>+</em><i>alphanumerical</i><em>+</em><i>documentation</i></span>
    );
    return (
      <span>
        <span className="dz-bim-vocab__strip" aria-hidden="true"><i>WIP</i><em>→</em><i>SHARED</i><em>→</em><i className="is-on">PUBLISHED</i><em>→</em><i>ARCHIVE</i></span>
        <span className="dz-viz-ba__sr">Container states: work in progress, then shared, then published, then archive.</span>
      </span>
    );
  }
  function Vocabulary({ delivers }) {
    const ref = V().useReveal(0.2);
    // the non-dimension vocabulary items (LOD, Level of Information Need, CDE)
    const extra = delivers.items.filter((t) => !/^\dD —/.test(t));
    return (
      <section className="dz-bim-vocab dz-viz-reveal" ref={ref} aria-labelledby="dz-bim-vocab-h">
        <Eyebrow>{delivers.eyebrow}</Eyebrow>
        <h2 id="dz-bim-vocab-h" className="dz-bim-h2">{delivers.title}</h2>
        <p className="dz-bim-lead">{delivers.sub}</p>
        <ul className="dz-bim-vocab__list">
          {extra.map((t, i) => {
            const dash = t.indexOf(' — ');
            const term = dash > 0 ? t.slice(0, dash) : '';
            const rest = dash > 0 ? t.slice(dash + 3) : t;
            return (
              <li key={i}>
                <span className="dz-bim-vocab__glyph" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{vocabGlyph(term)}</svg>
                </span>
                <span className="dz-bim-vocab__term">{term}</span>
                {vocabStrip(term)}
                <span className="dz-bim-vocab__def" dangerouslySetInnerHTML={{ __html: rest }} />
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  function Featured({ featured }) {
    const ref = V().useReveal(0.2);
    return (
      <section className="dz-bim-feat dz-viz-reveal" ref={ref} aria-labelledby="dz-bim-feat-h">
        <div className="dz-bim-feat__inner">
          <Eyebrow>{featured.eyebrow}</Eyebrow>
          <h2 id="dz-bim-feat-h" className="dz-bim-h2">{featured.title}</h2>
          <p className="dz-bim-lead">{featured.summary}</p>
          <ul className="dz-bim-feat__stats">
            {featured.stats.map((s, i) => (
              <li key={i} className="dz-bim-boot" style={{ '--bi': i }}><span className="dz-bim-feat__num">{s.num}</span><span className="dz-bim-feat__lbl">{s.label}</span></li>
            ))}
          </ul>
          <a className="dz-bim-feat__cta" href={featured.href}>{featured.ctaLabel}
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h9M8 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </a>
        </div>
      </section>
    );
  }

  /* Shared site FAQ pattern (matches /ai/, /twinmaq/, About, etc.): a light band
     with the "Show N questions" toggle revealing native <details> items. Grey
     variant per request. FAQPage JSON-LD in the shell head stays authoritative. */
  function Faq({ faq }) {
    return (
      <section className="dz-section dz-faq dz-faq--grey" aria-labelledby="dz-bim-faq-h">
        <div className="dz-section__inner">
          <span className="dz-eyebrow">{faq.eyebrow}</span>
          <h2 id="dz-bim-faq-h" className="dz-h2">{faq.title}</h2>
          <div className="dz-faq__list" id="dz-bim-faq-list">
            {faq.items.map((it, i) => (
              <details key={i} className="dz-faq-item">
                <summary>{it.q}</summary>
                <div className="dz-faq-item__body" dangerouslySetInnerHTML={{ __html: it.a }} />
              </details>
            ))}
          </div>
        </div>
      </section>
    );
  }

  /* ═══════════════ Page ═══════════════ */
  function BimPillarPage({ slug }) {
    const data = (window.DZ_SERVICE_LANDINGS || {})[slug || 'bim'];
    if (!data) {
      return <main id="dz-main" className="dz-bim"><section className="dz-bim-hero"><div className="dz-bim-hero__inner"><h1>BIM</h1><p>Content not loaded.</p></div></section></main>;
    }
    return (
      <main id="dz-main" className="dz-bim">
        <Hero hero={data.hero} />
        {window.DzProjectCards && <window.DzProjectCards data={data.projects} id="bim-projects" />}
        <WhatIs problem={data.problem} />
        <BimVsCad point={data.problem.points[1]} />
        <BimDimensions />
        <Vocabulary delivers={data.delivers} />
        <LodProgression />
        <GoldenThread approach={data.approach} />
        <DisciplinesHub heading={data.relatedHeading} stages={data.relatedStages} />
        <Featured featured={data.featured} />
        <Faq faq={data.faq} />
      </main>
    );
  }

  window.BimPillarPage = BimPillarPage;
})();
